<template>
	<div
		class="offer__presentation"
		:class="{
			'offer__presentation--overprovider': isOverProviderTitle,
			'grid-buybox': buyboxView === NewBuyBoxView.GRID,
		}"
		v-if="promotionOffer"
	>
		<!-- <i18n-t> is delicate and causes SSR issues. do not use v-if on it, take v-show instead -->
		<i18n-t
			v-show="isHighlighted"
			keypath="WEBAPP_WATCH_OTHER_FREE_ON_PROVIDER"
			tag="p"
			class="offer__presentation__info"
		>
			<template #other>
				<span class="offer__presentation__info__bold text-high-active">{{ highlightedCTAText }}</span>
			</template>
			<template #provider>
				<span>{{ promotionOffer.package.clearName }}</span>
				<DisclaimerButton
					v-if="showDisclaimerButton"
					class="ml-1"
					:price="packagePlan?.retailPrice"
					:disclaimerText="disclaimerText"
				/>
			</template>
		</i18n-t>

		<p v-show="!isHighlighted" class="offer__presentation__info">
			{{ subText }}
			<DisclaimerButton
				v-if="showDisclaimerButton"
				:price="packagePlan?.retailPrice"
				:disclaimerText="disclaimerText"
			/>
		</p>

		<!-- OVER_PROVIDER -->
		<BuyBoxOfferNewPresentation
			v-if="isOverProviderTitle && filterOption !== 'free'"
			:title="title"
			:offer="promotionOffer"
		/>
		<!-- OVER_PROVIDER -->
	</div>
</template>

<script lang="ts" setup>
import { ObjectType } from '@/@types/graphql-types';
import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import BuyBoxOfferNewPresentation from '@/components/experiments/OverProviderExp/components/BuyBoxOfferNewPresentation.vue';
import { CatalogueItem } from '@/helpers/promotion-helper';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { BuyBoxFilterOptions, NewBuyBoxView } from '@/stores/useUserStoreTypes';
import { computed, defineAsyncComponent } from 'vue';
import { PlanOffer } from '@/features/buybox/components/list/ListPromotionRow.vue';
import { useOverProviderExp } from '@/components/experiments/OverProviderExp';
import { getDisclaimerText } from '@/features/buybox/helpers/list-buybox-config';

const DisclaimerButton = defineAsyncComponent(() => import('@/features/buybox/components/DisclaimerButton.vue'));

const props = withDefaults(
	defineProps<{
		title: TitleDetail;
		promotionOffer: TitleOfferFragment | null;
		packagePlan: PlanOffer | null;
		catalogueItem: CatalogueItem | null;
		inPromotionCatalogue?: boolean;
		isHighlighted?: boolean;
		// OVER_PROVIDER_EXP
		filterOption?: BuyBoxFilterOptions;
		// OVER_PROVIDER_EXP
		buyboxView?: NewBuyBoxView;
	}>(),
	{ buyboxView: NewBuyBoxView.LIST }
);

const { t } = useI18n();

const { isOverProviderTitle } = useOverProviderExp({
	promotionOffer: () => props.promotionOffer,
});

const subText = computed(() => {
	if (!props.promotionOffer) return '';

	const provider = props.promotionOffer?.package.clearName;

	if (props.inPromotionCatalogue) {
		return t('WEBAPP_FREE_TRIAL_TEXT', { provider });
	}

	return props.title.objectType === ObjectType.Movie
		? t('WEBAPP_WATCH_SIMILAR_MOVIES_FREE_ON_X', { provider })
		: t('WEBAPP_WATCH_SIMILAR_TV_SHOWS_FREE_ON_X', { provider });
});

const highlightedCTAText = computed(() => {
	if (props.title.__typename === 'Show' || props.title.__typename === 'Season') return t('WEBAPP_OTHER_SHOWS');
	if (props.title.__typename === 'Movie') return t('WEBAPP_OTHER_MOVIES');
	return '';
});

const disclaimerText = computed(() => {
	if (!props.promotionOffer) return null;

	return getDisclaimerText(props.promotionOffer.package.shortName, props.promotionOffer.country);
});

const showDisclaimerButton = computed(() => {
	if (!!disclaimerText.value) return true;
	return props.promotionOffer?.package.packageId === 350 && props.packagePlan?.retailPrice;
});
</script>

<style lang="scss" scoped>
.offer__presentation {
	display: grid;
	width: 100%;

	// OVER_PROVIDER
	&--overprovider {
		gap: 8px;
		margin-bottom: -5px;
	}
	// OVER_PROVIDER

	@media (max-width: 1024px) {
		align-items: end;
		margin-top: 0;
	}
	&__info {
		margin: 0;
		margin-top: -2px;
		color: var(--jw-grey-200);
		font-size: 14px;
		line-height: 24px;
		max-height: 72px; /* Sibling flex items breaking overflow hiddenn without this */
		letter-spacing: 0.21px;
		display: -webkit-box;
		line-clamp: 3;
		-webkit-line-clamp: 3;
		-webkit-box-orient: vertical;
		overflow: hidden;
		text-overflow: ellipsis;
		overflow-wrap: break-word;

		@media (max-width: 1024px) {
			margin-top: 0;
		}

		@media (max-width: 450px) {
			line-clamp: 4;
			-webkit-line-clamp: 4;
			max-height: none;
		}

		&__bold {
			font-weight: 900;
		}
	}

	@media (max-width: 1024px) {
		align-items: start;
		margin-top: 0;
	}
	&.grid-buybox {
		.offer__presentation__info {
			font-size: 16px;
			color: #fff;
		}
	}
}
</style>
